import { zodResolver } from '@hookform/resolvers/zod';
import {
    Box, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, TextField,
} from '@mui/material';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useEffect } from 'react';
import { Form } from '@components/Form';
import MDButton from '@components/MDButton';
import { smallDialog } from 'assets/theme/dialogSize';
import { setLoading, useMaterialUIController } from 'context';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '@functions/api-error-message';
import { CrmTagChip } from '@layouts/pages/CRM/kanban-board/components/shared/crm-tag-chip';
import { TagService } from '../services/tag.service';
import { TagRequest } from '../services/requests/tag.request';
import { TagResponse } from '../services/responses/tag.response';
import { firstErrorMessage } from '@functions/first-error-message';

interface Props {
    open: boolean;
    edit: TagResponse | null;
    handleModal: () => void;
    getAll: () => void;
    clearSearch: () => void;
}

const DEFAULT_COLOR = '#3B82F6';
const HEX = /^#[0-9A-Fa-f]{6}$/;

export const TagModal = ({ open, edit, handleModal, getAll, clearSearch }: Props) => {
    const { t } = useTranslation();
    const [, dispatch] = useMaterialUIController();
    const { showAlert } = useAlert();
    const service = new TagService();

    const schema = z.object({
        name: z.string().trim().min(1, t('tags.nomeObrigatorio')).max(40, t('tags.nomeLongo')),
        color: z.string().regex(HEX, t('tags.corObrigatoria')),
    });

    const form = useForm<TagRequest>({
        resolver: zodResolver(schema),
        defaultValues: { name: '', color: DEFAULT_COLOR },
    });

    const { control, handleSubmit, reset, watch, formState: { errors } } = form;
    const color = watch('color');
    const name = watch('name');

    // Cor ainda sendo digitada não é hex válido; a prévia não pode quebrar.
    const previewColor = HEX.test(color ?? '') ? color : DEFAULT_COLOR;

    useEffect(() => {
        if (!open) return;

        reset(edit ? { name: edit.name, color: edit.color } : { name: '', color: DEFAULT_COLOR });
    }, [open]);

    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            showAlert(firstErrorMessage(errors) ?? t('general.camposObrigatorios'), 'error', 2500);
        }
    }, [errors]);

    const saveChanges = async (data: TagRequest) => {
        setLoading(dispatch, true);

        try {
            if (edit) {
                await service.update(edit.id, data);
                showAlert(t('general.atualizacao'), 'success', 2500);
                getAll();
            } else {
                await service.create(data);
                showAlert(t('general.cadastro'), 'success', 2500);
                clearSearch();
            }

            handleModal();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 3000);
        } finally {
            setLoading(dispatch, false);
        }
    };

    return (
        <Dialog open={open} onClose={handleModal} fullWidth maxWidth="sm" scroll="paper">
            <DialogTitle>{edit ? t('general.editar') : t('general.adicionar')}</DialogTitle>
            <FormProvider {...form}>
                <form onSubmit={handleSubmit(saveChanges)}>
                    <DialogContent style={smallDialog}>
                        <Grid container spacing={2}>
                            <Form.Field size={12}>
                                <FormControl fullWidth>
                                    <Form.Label>
                                        {t('general.nome')}
                                        <Form.MandatoryIcon />
                                    </Form.Label>
                                    <Form.Input
                                        type="text"
                                        autoComplete="off"
                                        size="medium"
                                        name="name"
                                        autoFocus
                                    />
                                    <Form.ErrorMessage field="name" />
                                </FormControl>
                            </Form.Field>

                            <Form.Field size={12}>
                                <FormControl fullWidth>
                                    <Form.Label>
                                        {t('tags.cor')}
                                        <Form.MandatoryIcon />
                                    </Form.Label>
                                    <Controller
                                        name="color"
                                        control={control}
                                        render={({ field }) => (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <TextField
                                                    {...field}
                                                    size="small"
                                                    placeholder="#RRGGBB"
                                                    error={!!errors.color}
                                                    sx={{ flex: 1 }}
                                                />
                                                <input
                                                    type="color"
                                                    value={previewColor}
                                                    onChange={(e) => field.onChange(e.target.value)}
                                                    style={{
                                                        width: 40,
                                                        height: 36,
                                                        border: 'none',
                                                        cursor: 'pointer',
                                                        background: 'none',
                                                        padding: 0,
                                                    }}
                                                />
                                            </Box>
                                        )}
                                    />
                                    <Form.ErrorMessage field="color" />
                                </FormControl>
                            </Form.Field>

                            {/* A cor no quadradinho não mostra como a tag fica no card; a prévia mostra. */}
                            <Form.Field size={12}>
                                <Form.Label>{t('tags.previa')}</Form.Label>
                                <Box>
                                    <CrmTagChip
                                        name={name?.trim() || t('tags.previaVazia')}
                                        color={previewColor}
                                    />
                                </Box>
                            </Form.Field>
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <MDButton
                            sx={{ marginRight: '5px' }}
                            variant="gradient"
                            color="dark"
                            onClick={handleModal}
                        >
                            {t('general.cancelar')}
                        </MDButton>
                        <MDButton variant="gradient" color="success" type="submit">
                            {t('general.salvar')}
                        </MDButton>
                    </DialogActions>
                </form>
            </FormProvider>
        </Dialog>
    );
};
